import type { StyledProps } from '@compiled/react';
import { styled, keyframes, css } from '@compiled/react';
import type { ChangeEventHandler, FocusEventHandler, KeyboardEventHandler } from 'react';
import React, { forwardRef } from 'react';

import { token } from '@atlaskit/tokens';

import { isFieldSizingSupported } from './utils/browserSupport.ts';

const shakeAnimation = keyframes({
	'0%, 100%': {
		transform: 'translateX(0)',
	},
	'20%, 60%': {
		transform: 'translateX(-10px)',
	},
	'40%, 80%': {
		transform: 'translateX(10px)',
	},
});

const fadeUnderlineAnimation = keyframes({
	'0%, 95%': {
		textDecoration: `solid underline ${token('color.background.accent.blue.subtle')} ${token('space.025')}`,
	},
	'100%': {
		textDecoration: `solid underline transparent ${token('space.025')}`,
	},
});

type EditorTitleTextAreaProps = {
	shouldAnimate: boolean;
	isTitleAIGenerated?: boolean;
	isTitleCenterAligned?: boolean;
	isDisabledTextBlack?: boolean;
};

type ExtendedEditorTitleTextAreaProps = EditorTitleTextAreaProps & {
	className?: string;
	disabled?: boolean;
	maxLength?: number;
	name?: string;
	onKeyDown?: KeyboardEventHandler;
	onChange?: ChangeEventHandler;
	onFocus?: FocusEventHandler;
	onBlur?: FocusEventHandler;
	placeholder?: string;
	ref?: React.LegacyRef<HTMLTextAreaElement>;
	required?: boolean;
	rows?: number;
	value?: string | ReadonlyArray<string> | number;
};

// TODO MODES-4231 Convert EditorTitleTextArea from styled to xcss once xcss supports keyframes
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-keyframes -- Ignored via go/DSP-18766
export const EditorTitleTextArea: React.ComponentType<
	EditorTitleTextAreaProps &
		React.ClassAttributes<HTMLTextAreaElement> &
		React.TextareaHTMLAttributes<HTMLTextAreaElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.textarea<EditorTitleTextAreaProps>({
	font: token('font.heading.xlarge'),
	animationName: shakeAnimation,
	animationTimingFunction: 'ease-in-out',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	animationDuration: (props: EditorTitleTextAreaProps) => (props.shouldAnimate ? '1000ms' : '0ms'),
	backgroundColor: 'inherit',
	fontWeight: token('font.weight.regular'),
	height: 'auto',
	display: 'block',
	border: 'none',
	textOverflow: 'ellipsis',
	resize: 'none',
	overflow: 'hidden',
	padding: 0,
	whiteSpace: 'pre-wrap',
	boxSizing: 'border-box',
	margin: 'auto',
	marginTop: 0,
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textAlign: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:disabled)': {
		color: token('color.text'),
	},
	'&:disabled': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: ({ isDisabledTextBlack }) =>
			isDisabledTextBlack ? token('color.text') : token('color.text.disabled'),
		background: 'transparent' /* fixing: disabled styles of FF cause a grey background */,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&::-ms-clear': {
		display: 'none',
	},
	'&:focus': {
		outline: 'none',
	},
	'&:required, &:invalid': {
		boxShadow: 'none',
	},
	'&::placeholder': {
		color: token('color.text.subtlest'),
		opacity: 1 /* firefox inputfield colour fix*/,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		textAlign: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	},
});
EditorTitleTextArea.displayName = 'EditorTitleTextArea';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const EditorTitleTextAreaNext: React.ComponentType<
	EditorTitleTextAreaProps &
		React.ClassAttributes<HTMLTextAreaElement> &
		React.TextareaHTMLAttributes<HTMLTextAreaElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled(EditorTitleTextArea)({
	fieldSizing: 'content',
});
EditorTitleTextAreaNext.displayName = 'EditorTitleTextAreaNext';

const aiGeneratedStyle = css({
	animationName: fadeUnderlineAnimation,
	animationDuration: '8s',
	animationDirection: 'forward',
});

export const AIGeneratedEditorTitleTextArea: React.ForwardRefExoticComponent<
	Omit<ExtendedEditorTitleTextAreaProps, 'ref'> & React.RefAttributes<HTMLTextAreaElement>
> = forwardRef<HTMLTextAreaElement, ExtendedEditorTitleTextAreaProps>(
	({ isTitleAIGenerated = false, ...props }, ref) => {
		if (isFieldSizingSupported()) {
			return (
				<EditorTitleTextAreaNext
					css={isTitleAIGenerated && aiGeneratedStyle}
					{...props}
					ref={ref}
				/>
			);
		} else {
			return (
				<EditorTitleTextArea css={isTitleAIGenerated && aiGeneratedStyle} {...props} ref={ref} />
			);
		}
	},
);
AIGeneratedEditorTitleTextArea.displayName = 'AIGeneratedEditorTitleTextArea';
